import axios from 'axios';
import * as Sentry from '@sentry/react';
import get from 'lodash/get';
import pick from 'lodash/pick';
import find from 'lodash/find';
import uuidv4 from 'uuid/v4';
import queryString from 'query-string';
import posthog from 'posthog-js';

import {SWIPE_TRACKER_URL, SWIPE_TRACKER_X_API_KEY, POSTHOG_API_KEY, USERPILOT_TOKEN} from '../constants';
import {getCurrentHostname, isPrcHostname} from '../utils/host-utils';
import {SWIPE_TRACKER_EVENTS, COOKIE_PREFERENCE_OPTIONS} from '../enums';
import {anonymousUserId} from '../utils/data-utils';
import {navigationTracking, getQueryParamsAsString} from '../utils/generic-utils';
import eventsMap from '../analytics-event-mappings';
import {makeObservable, computed} from 'mobx';

const {YES} = COOKIE_PREFERENCE_OPTIONS;

class BaseAnalyticsStore {
  analyticsServer = undefined;
  trackEvents = false;
  swipeTrackerUrl = undefined;
  swipeTrackerXApiKey = undefined;
  store = undefined;
  posthogIsLoaded = false;

  constructor() {
    this.swipeTrackerUrl = SWIPE_TRACKER_URL;
    this.swipeTrackerXApiKey = SWIPE_TRACKER_X_API_KEY;

    const parsedQuery = queryString.parse(location.search);
    this.sessionId = parsedQuery.session_id || uuidv4();

    // delete session_id from url if present after redirect
    if (parsedQuery.session_id && window.history.replaceState) {
      delete parsedQuery.session_id;
      window.history.replaceState(
        parsedQuery,
        document.title,
        location.origin + location.pathname + getQueryParamsAsString(parsedQuery)
      );
    }

    const currentHostname = getCurrentHostname();
    const shouldTrackAnalytics = [
      this.swipeTrackerUrl,
      this.swipeTrackerXApiKey,
      !isPrcHostname(currentHostname)
    ].every(Boolean);

    if (shouldTrackAnalytics) {
      this.analyticsServer = axios.create({
        headers: {'x-api-key': SWIPE_TRACKER_X_API_KEY}
      });
      this.trackEvents = true;
    }

    makeObservable(this);
  }

  setStore = store => {
    this.store = store;
  };

  canEnablePosthog = () => {
    const {cookiePreference} = this.store;
    cookiePreference.loadCookieData();
    const allowsTrackingCookies = cookiePreference.cookiePreference === YES;
    const currentHostname = getCurrentHostname();
    return Boolean(POSTHOG_API_KEY) && !isPrcHostname(currentHostname) && allowsTrackingCookies;
  };

  @computed
  get userpilotEnabled() {
    return this.store.cookiePreference.cookiePreference === COOKIE_PREFERENCE_OPTIONS.YES && !!USERPILOT_TOKEN;
  }

  initializePosthog = async () => {
    if (Boolean(POSTHOG_API_KEY) && !this.posthogIsLoaded) {
      await new Promise(resolve => {
        posthog.init(POSTHOG_API_KEY, {
          api_host: 'https://app.posthog.com',
          autocapture: false,
          loaded: () => {
            this.posthogIsLoaded = true;
          }
        });
        resolve();
      });
    }
  };

  enablePosthog = async app => {
    if (this.canEnablePosthog()) {
      await this.initializePosthog();

      const platformSlug = this._getPlatformSlug();
      const workspaceSlug = this._getDomainSlug();

      const {auth} = this.store;
      const {user} = auth;

      const userId = (user && user.id) || anonymousUserId();

      const visitor = {};
      const config = {
        application: app,
        platform: platformSlug
      };

      if (user && user.email) {
        visitor.userId = userId;
        visitor.isEditor = user.isEditor;
        visitor.isAdmin = user.isAdmin;
        visitor.isPlatformAdmin = user.isPlatformAdmin;
      }

      if (app === 'VIEWER_DESKTOP' || app === 'VIEWER_MOBILE') {
        config.workspace = workspaceSlug;
      }

      posthog.identify(userId, visitor);
      posthog.register(config);
      posthog.set_config({
        disable_persistence: false
      });
    }
  };

  disablePosthog = async () => {
    await this.initializePosthog();

    if (this.posthogIsLoaded) {
      posthog.set_config({
        disable_persistence: true
      });
    }
  };

  // track page view events
  trackPageViewEvent = params => {
    navigationTracking.add(document.location.toString());
    return this._trackEvent({
      event: SWIPE_TRACKER_EVENTS.PAGEVIEW,
      ...params
    });
  };

  // track action events
  trackActionEvent = params => {
    navigationTracking.add(document.location.toString());
    return this._trackEvent({
      event: SWIPE_TRACKER_EVENTS.ACTION,
      ...params
    });
  };

  // track search events
  trackSearchEvent = params =>
    this._trackEvent({
      event: SWIPE_TRACKER_EVENTS.SEARCH,
      ...params
    });

  _trackEvent = ({event, objectsInfo = {}, action = '', actionContent = ''}) => {
    // dont track events locally
    if (!this.trackEvents) {
      return;
    }

    const data = {
      event,
      objectsInfo,
      action,
      actionContent,
      ...this._getAnalyticsData(),
      platformSlug: this._getPlatformSlug(),
      domainSlug: this._getDomainSlug(),
      ...this._getReferrerUrl(action)
    };

    this._fireEvent(data);
  };

  _getPlatformSlug = () => {
    const {platform} = this.store;
    const {platformSlug} = platform;
    return platformSlug;
  };

  _getDomainSlug = () => {
    const url = window.location.hostname;
    const urlParts = url.split('.');
    const subdomain = urlParts[0];
    const subDomainParts = subdomain.split('-');
    if (subDomainParts.length === 1) {
      const {platform} = this.store;
      const {defaultDomainSlug} = platform;
      return defaultDomainSlug;
    }
    const domainSlug = subDomainParts[0];
    return domainSlug;
  };

  _getAnalyticsData = () => {
    const {
      auth: {user},
      platform
    } = this.store;

    // if no anonymousId, returns a randomly generated one
    const anonymousId = anonymousUserId();

    let userId;
    if (user) userId = get(user, 'id');

    const language = platform.currentLanguage;
    const timestamp = new Date().toJSON();
    const url = window.location.href;

    return {
      anonymousId,
      sessionId: this.sessionId,
      language,
      timestamp,
      authId: userId,
      url
    };
  };

  _fireEvent = data => {
    return this.analyticsServer.post(SWIPE_TRACKER_URL, data).catch(error => {
      Sentry.captureException(error, {extra: data});
    });
  };

  _getReferrerUrl = action => {
    if (!this.isReferrerNeeded(action)) {
      return {referrerUrl: null};
    }

    return {referrerUrl: navigationTracking.referrerUrl};
  };

  isReferrerNeeded = action => {
    const eventsWithoutReferrer = pick(eventsMap, [
      'categoryNavigate',
      'categoryChange',
      'changeLanguage',
      'closeHintByButton',
      'closeHintByBackground',
      'openHint',
      'search',
      'cancelSearch'
    ]);

    return find(eventsWithoutReferrer, x => x.action === action) ? false : true;
  };
}

export default BaseAnalyticsStore;
